import { useEffect, useRef, useState } from "react";
import type {
  FreeeStatusResponse,
  InventoryAutoResponse,
  InventoryMonthEndRow,
  PlImportsResponse,
  PlSetting,
  PrepaymentRow,
  PrepaymentsResponse,
} from "../../../types/pl";
import { StateBanner } from "../../../components/StateBanner";
import {
  fetchPlFreeeStatus,
  fetchPlPrepayments,
  fetchPlSettings,
  postPlFreeeAuth,
  postPlImportRakutenAdPurchase,
  postPlImportRakutenAffiliate,
  postPlImportRakutenExpense,
  postPlImportRakutenPoints,
  postPlInventoryMonthEnd,
  postPlInventoryMonthEndAuto,
  postPlLogiMonthly,
  postPlPrepayment,
  postPlSetting,
  postPlSync,
  ApiClientError,
  type PlSyncWhat,
} from "../../../api/client";
import {
  chunkRakutenPoints,
  parseCsvFile,
  type CsvKind,
  type ParsedCsv,
  type RakutenAdPurchaseParsed,
  type RakutenAffiliateParsed,
  type RakutenExpenseParsed,
  type RakutenPointsParsed,
} from "../../../lib/plCsv";
import { addMonths } from "../../../lib/plChart";
import { yen } from "../../../lib/plFormat";

const KIND_LABEL: Record<string, string> = {
  rakuten_expense: "楽天 月次収支見込みCSV",
  rakuten_points: "楽天 ポイント付与履歴CSV",
  rakuten_affiliate: "楽天 アフィリエイト パートナーレポートCSV",
  rakuten_ad_purchase: "楽天 広告購入履歴CSV（RPP/CPA/TDA/楽天市場広告）",
  logi_monthly: "ロジクム月額（送料・資材）",
  cost_snapshot: "原価スナップショット（oem-system）",
  meta_spend: "Meta 日次広告費",
  google_spend: "Google 日次広告費",
  order_finance: "注文単位の経済性（決済手数料・値引）",
  freee: "freee 月次試算表（P/L・B/S）",
  unknown: "不明なファイル",
};

const STATUS_LABEL: Record<string, string> = {
  actual: "確定",
  estimate: "見込み",
  missing: "未取込",
};

// ---------------------------------------------------------------------------
// (a) CSVドロップ／選択・判定・プレビュー・取込
// ---------------------------------------------------------------------------

interface FileEntry {
  id: string;
  filename: string;
  kind: CsvKind;
  parsed: ParsedCsv | null;
  warnings: string[];
  selectedMonth: string;
  status: "ready" | "importing" | "done" | "error";
  resultText?: string;
  errorMessage?: string;
}

function todayMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function CsvImportPanel() {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function addFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const parsedEntries: FileEntry[] = await Promise.all(
      files.map(async (file, i) => {
        try {
          const r = await parseCsvFile(file);
          const inferredMonth = r.parsed && "month" in r.parsed && r.parsed.month ? r.parsed.month : todayMonth();
          return {
            id: `${Date.now()}-${i}-${file.name}`,
            filename: r.filename,
            kind: r.kind,
            parsed: r.parsed,
            warnings: r.warnings,
            selectedMonth: inferredMonth,
            status: "ready" as const,
          };
        } catch (e) {
          return {
            id: `${Date.now()}-${i}-${file.name}`,
            filename: file.name,
            kind: "unknown" as CsvKind,
            parsed: null,
            warnings: [],
            selectedMonth: todayMonth(),
            status: "error" as const,
            errorMessage: e instanceof Error ? e.message : "ファイルの読み込みに失敗しました",
          };
        }
      })
    );
    setEntries((prev) => [...prev, ...parsedEntries]);
  }

  function removeEntry(id: string) {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  function setMonth(id: string, month: string) {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, selectedMonth: month } : e)));
  }

  async function importEntry(entry: FileEntry) {
    if (!entry.parsed) return;
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, status: "importing", errorMessage: undefined } : e)));
    try {
      let resultText = "";
      if (entry.kind === "rakuten_expense") {
        const p = entry.parsed as RakutenExpenseParsed;
        const month = p.month ?? entry.selectedMonth;
        const res = await postPlImportRakutenExpense({
          month,
          rows: p.rows,
          source_file: entry.filename,
        });
        resultText = `取込 ${res.imported}件・バケット別合計 ${Object.entries(res.buckets)
          .map(([k, v]) => `${k}:${yen(v)}`)
          .join(" / ")}${res.unknown_items.length ? ` ・未知品目 ${res.unknown_items.length}件` : ""}`;
      } else if (entry.kind === "rakuten_points") {
        const p = entry.parsed as RakutenPointsParsed;
        const chunks = chunkRakutenPoints(p.rows, 5000);
        let imported = 0;
        let matched = 0;
        let totalPoints = 0;
        const unmatched: string[] = [];
        for (const chunk of chunks) {
          const res = await postPlImportRakutenPoints({ rows: chunk });
          imported += res.imported;
          matched += res.matched;
          totalPoints += res.total_points;
          unmatched.push(...res.unmatched);
        }
        resultText = `取込 ${imported}件・一致 ${matched}件${unmatched.length ? ` ・未一致 ${unmatched.length}件（${unmatched.slice(0, 5).join(", ")}${unmatched.length > 5 ? "…" : ""}）` : ""}・付与pt合計 ${totalPoints.toLocaleString("ja-JP")}pt${chunks.length > 1 ? `（${chunks.length}分割送信）` : ""}`;
      } else if (entry.kind === "rakuten_affiliate") {
        const p = entry.parsed as RakutenAffiliateParsed;
        const month = p.month ?? entry.selectedMonth;
        await postPlImportRakutenAffiliate({
          month,
          clicks: p.clicks,
          sales_count: p.sales_count,
          sales_amount: p.sales_amount,
          rewards: p.rewards,
          partner_rows: p.partner_rows,
        });
        resultText = `${month} 取込完了・clicks ${p.clicks.toLocaleString("ja-JP")} / sales ${p.sales_count}件 ${yen(p.sales_amount)} / rewards ${yen(p.rewards)}`;
      } else if (entry.kind === "rakuten_ad_purchase") {
        const p = entry.parsed as RakutenAdPurchaseParsed;
        const month = p.month ?? entry.selectedMonth;
        const res = await postPlImportRakutenAdPurchase({
          month,
          rows: p.rows,
          source_file: entry.filename,
        });
        resultText = `${month} 取込 ${res.imported}件・種別別合計 ${Object.entries(p.preview.byType)
          .map(([k, v]) => `${k}:${yen(v)}`)
          .join(" / ")}`;
      } else {
        throw new Error("この種別は取込できません");
      }
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, status: "done", resultText } : e)));
    } catch (e) {
      const message = e instanceof ApiClientError ? e.message : e instanceof Error ? e.message : "取込に失敗しました";
      setEntries((prev) => prev.map((en) => (en.id === entry.id ? { ...en, status: "error", errorMessage: message } : en)));
    }
  }

  return (
    <div className="pl-card">
      <span className="pl-card-title">CSV取込</span>
      <span className="pl-card-sub">
        楽天RMSからダウンロードした4種のCSV（月次収支見込み／ポイント付与履歴／アフィリエイト パートナーレポート／広告購入履歴）をドロップまたは選択してください。文字コード（cp932/UTF-8 BOM）は自動判定します。
      </span>

      <div
        className={`pl-dropzone ${dragOver ? "drag-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
      >
        <span>ここにCSVをドロップ、またはクリックして選択（複数可）</span>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.txt"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            void addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {entries.length > 0 && (
        <div className="pl-csv-entries">
          {entries.map((entry) => (
            <div key={entry.id} className="pl-csv-entry">
              <div className="pl-csv-entry-head">
                <span className="pl-csv-entry-name">{entry.filename}</span>
                <span className={`pl-badge ${entry.kind === "unknown" ? "pl-badge--unconfirmed" : ""}`}>
                  {KIND_LABEL[entry.kind] ?? entry.kind}
                </span>
                <button className="btn btn-sm" onClick={() => removeEntry(entry.id)} disabled={entry.status === "importing"}>
                  削除
                </button>
              </div>

              {entry.warnings.length > 0 && (
                <div className="pl-csv-warnings">
                  {entry.warnings.map((w, i) => (
                    <div key={i}>⚠ {w}</div>
                  ))}
                </div>
              )}

              {entry.parsed && <CsvPreview parsed={entry.parsed} />}

              {entry.parsed && (entry.parsed.kind === "rakuten_affiliate" || entry.parsed.kind === "rakuten_ad_purchase") && !entry.parsed.month && (
                <label className="pl-csv-month-picker">
                  対象月:
                  <input
                    type="month"
                    className="pl-input"
                    value={entry.selectedMonth}
                    onChange={(e) => setMonth(entry.id, e.target.value)}
                  />
                </label>
              )}

              <div className="pl-csv-entry-foot">
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!entry.parsed || entry.status === "importing" || entry.status === "done"}
                  onClick={() => void importEntry(entry)}
                >
                  {entry.status === "importing" ? "取込中…" : entry.status === "done" ? "取込済み" : "取込"}
                </button>
                {entry.status === "done" && entry.resultText && <span className="pl-csv-result pl-csv-result--ok">{entry.resultText}</span>}
                {entry.status === "error" && entry.errorMessage && <span className="pl-csv-result pl-csv-result--error">{entry.errorMessage}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CsvPreview({ parsed }: { parsed: ParsedCsv }) {
  if (parsed.kind === "rakuten_expense") {
    const statusText = Object.entries(parsed.preview.statusCounts)
      .map(([k, v]) => `${k} ${v}件`)
      .join(" / ");
    return (
      <div className="pl-csv-preview">
        {parsed.month ?? "月不明"}・品目 {parsed.preview.itemCount}件・合計 {yen(parsed.preview.totalAmount)}
        {statusText ? `（${statusText}）` : ""}
      </div>
    );
  }
  if (parsed.kind === "rakuten_points") {
    const byMonth = Object.entries(parsed.preview.byMonth)
      .map(([m, v]) => `${m}: ${v.count}件 ${v.points.toLocaleString("ja-JP")}pt`)
      .join(" / ");
    return (
      <div className="pl-csv-preview">
        {parsed.preview.count}件・合計 {parsed.preview.totalPoints.toLocaleString("ja-JP")}pt
        {byMonth ? `（${byMonth}）` : ""}
      </div>
    );
  }
  if (parsed.kind === "rakuten_affiliate") {
    return (
      <div className="pl-csv-preview">
        {parsed.month ?? "月不明"}・clicks {parsed.clicks.toLocaleString("ja-JP")} / sales {parsed.sales_count}件 {yen(parsed.sales_amount)} / rewards {yen(parsed.rewards)}（パートナー{parsed.partner_rows}件）
      </div>
    );
  }
  if (parsed.kind === "rakuten_ad_purchase") {
    const byType = Object.entries(parsed.preview.byType)
      .map(([k, v]) => `${k}: ${yen(v)}`)
      .join(" / ");
    return (
      <div className="pl-csv-preview">
        {parsed.month ?? "月不明"}・{parsed.rows.length}件・合計 {yen(parsed.preview.total)}（{byType}）
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// (a.5) 今すぐ更新（POST /pl/sync what=cost/meta/finance・review §2-7 の穴埋め）
// ---------------------------------------------------------------------------

const SYNC_WHATS: { key: PlSyncWhat; label: string }[] = [
  { key: "cost", label: "原価スナップショット" },
  { key: "meta", label: "Meta広告費" },
  { key: "google", label: "Google広告費" },
  { key: "finance", label: "注文単位の経済性" },
];

/** Google 広告は 2026-10-14 配信開始（stoq-ad/google-ads）。過去分バックフィルの既定開始日。 */
const GOOGLE_ADS_START = "2026-10-14";

function SyncNowPanel() {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Record<string, "ok" | "error">>({});
  const [errorText, setErrorText] = useState<string | null>(null);

  async function runAll() {
    setBusy(true);
    setErrorText(null);
    setResults({});
    const next: Record<string, "ok" | "error"> = {};
    for (const w of SYNC_WHATS) {
      try {
        await postPlSync(w.key);
        next[w.key] = "ok";
      } catch (e) {
        next[w.key] = "error";
        setErrorText(e instanceof ApiClientError ? e.message : "同期に失敗しました");
      }
      setResults({ ...next });
    }
    setBusy(false);
  }

  // Meta 過去分バックフィル（初回セットアップ用）。既定の同期は直近3日のみなので、8/1 以降を一括取得する
  const [backfill, setBackfill] = useState<"idle" | "busy" | "ok" | "error">("idle");
  async function runMetaBackfill() {
    setBackfill("busy");
    setErrorText(null);
    try {
      await postPlSync("meta", "2026-08-01");
      setBackfill("ok");
    } catch (e) {
      setBackfill("error");
      setErrorText(e instanceof ApiClientError ? e.message : "Meta過去分の取得に失敗しました");
    }
  }

  // Google 過去分バックフィル（2026-10-14 配信開始〜）。既定の同期は直近3日のみ
  const [googleBackfill, setGoogleBackfill] = useState<"idle" | "busy" | "ok" | "error">("idle");
  async function runGoogleBackfill() {
    setGoogleBackfill("busy");
    setErrorText(null);
    try {
      await postPlSync("google", GOOGLE_ADS_START);
      setGoogleBackfill("ok");
    } catch (e) {
      setGoogleBackfill("error");
      setErrorText(e instanceof ApiClientError ? e.message : "Google過去分の取得に失敗しました");
    }
  }

  return (
    <div className="pl-card">
      <span className="pl-card-title">今すぐ更新</span>
      <span className="pl-card-sub">
        原価スナップショット・Meta広告費・Google広告費・注文単位の経済性（決済手数料・値引）をworkerに即時同期します（通常は5分tickで自動更新）。
      </span>
      <div className="pl-csv-entry-foot">
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void runAll()}>
          {busy ? "更新中…" : "今すぐ更新"}
        </button>
        {SYNC_WHATS.map(
          (w) =>
            results[w.key] && (
              <span
                key={w.key}
                className={`pl-csv-result ${results[w.key] === "ok" ? "pl-csv-result--ok" : "pl-csv-result--error"}`}
              >
                {w.label}: {results[w.key] === "ok" ? "完了" : "失敗"}
              </span>
            )
        )}
      </div>
      <div className="pl-csv-entry-foot">
        <button className="btn btn-sm" disabled={busy || backfill === "busy"} onClick={() => void runMetaBackfill()}>
          {backfill === "busy" ? "取得中…" : "Meta 過去分を取得（2026-08-01〜）"}
        </button>
        {backfill === "ok" && <span className="pl-csv-result pl-csv-result--ok">Meta 過去分: 完了</span>}
        {backfill === "error" && <span className="pl-csv-result pl-csv-result--error">Meta 過去分: 失敗</span>}
      </div>
      <div className="pl-csv-entry-foot">
        <button className="btn btn-sm" disabled={busy || googleBackfill === "busy"} onClick={() => void runGoogleBackfill()}>
          {googleBackfill === "busy" ? "取得中…" : `Google 過去分を取得（${GOOGLE_ADS_START}〜）`}
        </button>
        {googleBackfill === "ok" && <span className="pl-csv-result pl-csv-result--ok">Google 過去分: 完了</span>}
        {googleBackfill === "error" && <span className="pl-csv-result pl-csv-result--error">Google 過去分: 失敗</span>}
      </div>
      {errorText && <span className="pl-csv-result pl-csv-result--error">{errorText}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// (a.6) freee 連携（財務タブの元データ）・月末在庫金額
// ---------------------------------------------------------------------------

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("ja-JP");
}

interface FreeeSyncResult {
  months: string[];
  rows: number;
  errors: { month: string; report: string; error: string }[];
}

function FreeeConnectPanel() {
  const [status, setStatus] = useState<FreeeStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<FreeeSyncResult | null>(null);

  useEffect(() => {
    fetchPlFreeeStatus()
      .then(setStatus)
      .catch((e: unknown) => setLoadError(e instanceof ApiClientError ? e.message : "freee 連携状態の取得に失敗しました"));
  }, []);

  // 権限を増やした後などは「連携済み」でも再認可が要る → 手動で認可欄を開ける
  const [reauthOpen, setReauthOpen] = useState(false);

  async function connect() {
    setAuthBusy(true);
    setAuthError(null);
    try {
      const res = await postPlFreeeAuth(code.trim());
      setStatus(res);
      setCode("");
      setReauthOpen(false);
    } catch (e) {
      setAuthError(e instanceof ApiClientError ? e.message : "連携に失敗しました");
    } finally {
      setAuthBusy(false);
    }
  }

  async function syncNow() {
    setSyncBusy(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const res = await postPlSync("freee");
      setSyncResult(res.result as FreeeSyncResult);
      // 同期後の最終同期時刻・エラー表示を更新する
      const fresh = await fetchPlFreeeStatus();
      setStatus(fresh);
    } catch (e) {
      setSyncError(e instanceof ApiClientError ? e.message : "同期に失敗しました");
    } finally {
      setSyncBusy(false);
    }
  }

  const needsAuth = status != null && (!status.connected || (status.last_error ?? "").includes("invalid_grant") || reauthOpen);

  return (
    <div className="pl-card">
      <span className="pl-card-title">freee 連携（財務タブ）</span>
      <span className="pl-card-sub">
        freee の月次試算表（P/L・B/S）を毎朝自動取得して「財務」タブに表示します。読取専用の連携で、freee への書き込みはしません。
      </span>
      {loadError && <span className="pl-csv-result pl-csv-result--error">{loadError}</span>}
      {!status && !loadError && <div className="pl-empty">読み込み中…</div>}
      {status && (
        <>
          <div className="pl-freee-badges">
            <span className={`pl-badge ${status.configured ? "pl-badge--ok" : "pl-badge--unconfirmed"}`}>
              {status.configured ? "アプリ設定済み" : "アプリ未設定（FREEE_CLIENT_ID/SECRET）"}
            </span>
            <span className={`pl-badge ${status.connected ? "pl-badge--ok" : "pl-badge--unconfirmed"}`}>
              {status.connected ? "連携済み" : "未連携"}
            </span>
          </div>
          <div className="pl-freee-meta">
            <span>トークン期限: {fmtTs(status.token_expires_at)}</span>
            <span>最終同期: {fmtTs(status.last_sync_at)}</span>
            <span>取込済み月: {status.months_available.length > 0 ? status.months_available.join(", ") : "なし"}</span>
          </div>
          {status.last_error && (
            <span className="pl-csv-result pl-csv-result--error">直近のエラー: {status.last_error}</span>
          )}

          {needsAuth && (
            <>
              {status.authorize_url ? (
                <div className="pl-csv-entry-foot">
                  <a href={status.authorize_url} target="_blank" rel="noopener noreferrer">
                    freee で認可（別タブで開く）
                  </a>
                  <span className="pl-card-sub">許可すると表示される認可コード（有効10分）を下に貼り付けてください。</span>
                </div>
              ) : (
                <span className="pl-csv-result pl-csv-result--error">認可URLを作れません。worker に FREEE_CLIENT_ID が設定されているか確認してください。</span>
              )}
              <div className="pl-freee-code-row">
                <input
                  type="text"
                  className="pl-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="認可コード"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button className="btn btn-primary btn-sm" disabled={authBusy || !code.trim()} onClick={() => void connect()}>
                  {authBusy ? "連携中…" : "連携"}
                </button>
              </div>
              {authError && <span className="pl-csv-result pl-csv-result--error">{authError}</span>}
            </>
          )}

          {status.connected && !reauthOpen && (
            <div className="pl-csv-entry-foot">
              <button className="btn btn-sm" onClick={() => setReauthOpen(true)}>
                再認可（freee アプリの権限を変えたとき）
              </button>
            </div>
          )}
          {status.connected && (
            <div className="pl-csv-entry-foot">
              <button className="btn btn-primary btn-sm" disabled={syncBusy} onClick={() => void syncNow()}>
                {syncBusy ? "同期中…（最大1分ほど）" : "freee 試算表を今すぐ同期"}
              </button>
              {syncError && <span className="pl-csv-result pl-csv-result--error">{syncError}</span>}
            </div>
          )}
          {syncResult && (
            <div className="pl-csv-preview">
              同期完了: {syncResult.months.length}か月（{syncResult.months.join(", ") || "対象なし"}）・{syncResult.rows.toLocaleString("ja-JP")}行
              {syncResult.errors.length > 0 && (
                <div className="pl-csv-warnings">
                  {syncResult.errors.map((er, i) => (
                    <div key={i}>
                      ⚠ {er.month} {er.report}: {er.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** 会計期間の期首（2026-06）から今月まで。新しい月を既定にする */
function fiscalMonthOptions(): string[] {
  const cur = todayMonth();
  const out: string[] = [];
  let m = "2026-06";
  for (let i = 0; m <= cur && i < 24; i++) {
    out.push(m);
    m = addMonths(m, 1);
  }
  return out.length > 0 ? out : ["2026-06"];
}

function InventoryMonthEndForm() {
  const options = fiscalMonthOptions();
  // 既定は前月末（今月が期首月なら期首月）
  const [month, setMonth] = useState(options.length >= 2 ? options[options.length - 2] : options[0]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [rows, setRows] = useState<InventoryMonthEndRow[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [resultText, setResultText] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoResult, setAutoResult] = useState<InventoryAutoResponse | null>(null);
  const [autoError, setAutoError] = useState<string | null>(null);

  useEffect(() => {
    fetchPlFreeeStatus()
      .then((s) => setRows(s.inventory))
      .catch(() => setRows([]));
  }, []);

  async function runAuto(force: boolean) {
    setAutoBusy(true);
    setAutoError(null);
    setResultText(null);
    setErrorText(null);
    if (!force) setAutoResult(null);
    try {
      const res = await postPlInventoryMonthEndAuto({ month, force: force || undefined });
      setAutoResult(res);
      setRows(res.inventory);
    } catch (e) {
      setAutoError(e instanceof ApiClientError ? e.message : "自動計算に失敗しました");
    } finally {
      setAutoBusy(false);
    }
  }

  async function submit() {
    setSaving(true);
    setResultText(null);
    setErrorText(null);
    try {
      const res = await postPlInventoryMonthEnd({
        month,
        amount_jpy: Math.round(Number(amount)),
        note: note.trim() || undefined,
      });
      setRows(res.inventory);
      setResultText(`${month} 末の在庫金額を保存しました`);
    } catch (e) {
      setErrorText(e instanceof ApiClientError ? e.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  }

  const amountValid = amount !== "" && Number.isFinite(Number(amount)) && Number(amount) >= 0;

  return (
    <div className="pl-card">
      <span className="pl-card-title">月末在庫金額（財務タブ「営業利益（販売原価ベース）」の材料）</span>
      <span className="pl-card-sub">
        月末の倉庫在庫の金額（円）。毎月1日に倉庫在庫×原価から自動で入りますが、入荷未反映などがあるときはここで上書きしてください（手入力が優先されます）。自動は倉庫在庫のみで、在途品（輸入仕入高・仕入諸掛・輸入運賃のうち、倉庫の在庫報告にまだ反映されていないもの）は下の「前払い仕入（在途品）」カードで管理します。財務タブの月末在庫は倉庫在庫＋在途品です。
      </span>
      <div className="pl-form-grid">
        <label className="pl-form-field">
          対象月（月末）
          <select className="pl-input" value={month} onChange={(e) => setMonth(e.target.value)}>
            {options.map((m) => (
              <option key={m} value={m}>
                {m.slice(0, 4)}年{Number(m.slice(5, 7))}月末
              </option>
            ))}
          </select>
        </label>
        <label className="pl-form-field">
          在庫金額（円）
          <input type="number" min={0} className="pl-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="12000000" />
        </label>
        <label className="pl-form-field pl-form-field--wide">
          メモ
          <input type="text" className="pl-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="例: 在途品を含めて手入力" />
        </label>
      </div>
      <div className="pl-csv-entry-foot">
        <button className="btn btn-primary btn-sm" disabled={saving || autoBusy || !amountValid} onClick={() => void submit()}>
          {saving ? "保存中…" : "保存"}
        </button>
        <button className="btn btn-sm" disabled={saving || autoBusy} onClick={() => void runAuto(false)}>
          {autoBusy ? "計算中…" : "倉庫在庫から計算して保存"}
        </button>
        {resultText && <span className="pl-csv-result pl-csv-result--ok">{resultText}</span>}
        {errorText && <span className="pl-csv-result pl-csv-result--error">{errorText}</span>}
        {autoError && <span className="pl-csv-result pl-csv-result--error">{autoError}</span>}
      </div>
      {autoResult && (
        <div className="pl-csv-preview">
          {autoResult.computed.month} 末の倉庫在庫: <strong>{yen(autoResult.computed.amount)}</strong>（SKU {autoResult.computed.matched}件）
          <div>
            使用ファイル: {autoResult.computed.file}・報告時刻 {fmtTs(autoResult.computed.report_at)}
          </div>
          {autoResult.written ? (
            <div className="pl-csv-result pl-csv-result--ok">保存しました（自動）。</div>
          ) : (
            <div className="pl-csv-entry-foot">
              <span className="pl-csv-result pl-csv-result--error">手入力の値があります。上書きしますか？</span>
              <button className="btn btn-sm btn-danger" disabled={autoBusy} onClick={() => void runAuto(true)}>
                {autoBusy ? "上書き中…" : "上書き"}
              </button>
            </div>
          )}
          {autoResult.computed.missing.length > 0 && (
            <div className="pl-csv-warnings">
              <div>⚠ 原価未登録のSKU（金額に含まれていません）: {autoResult.computed.missing.join(", ")}</div>
            </div>
          )}
          {autoResult.computed.warnings.length > 0 && (
            <div className="pl-csv-warnings">
              {autoResult.computed.warnings.map((w, i) => (
                <div key={i}>⚠ {w}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="pl-table-wrap">
          <table className="data-table pl-import-table">
            <thead>
              <tr>
                <th>月末</th>
                <th>金額</th>
                <th>由来</th>
                <th>メモ</th>
                <th>更新</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month}>
                  <td>{r.month}</td>
                  <td>{yen(r.amount_jpy)}</td>
                  <td>
                    <span className={`pl-badge ${r.source === "manual" ? "pl-badge--ok" : ""}`}>{r.source === "manual" ? "手入力" : "自動"}</span>
                  </td>
                  <td style={{ whiteSpace: "normal" }}>{r.note ?? "—"}</td>
                  <td>{fmtTs(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows && rows.length === 0 && <div className="pl-empty">まだ登録がありません。</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// (a.7) 前払い仕入（在途品）台帳
// ---------------------------------------------------------------------------

const SOURCE_BADGE: Record<PrepaymentRow["source"], { label: string; cls: string }> = {
  freee_deal: { label: "取引", cls: "pl-badge--src-deal" },
  freee_mj: { label: "振替伝票", cls: "pl-badge--src-mj" },
  manual: { label: "手入力", cls: "pl-badge--ok" },
};

function PrepaymentRowView({
  row,
  busy,
  onSave,
  onDelete,
}: {
  row: PrepaymentRow;
  busy: boolean;
  onSave: (id: string, arrivedOn: string | null, note: string) => void;
  onDelete: (row: PrepaymentRow) => void;
}) {
  const [note, setNote] = useState(row.note ?? "");
  useEffect(() => setNote(row.note ?? ""), [row.note]);
  const badge = SOURCE_BADGE[row.source];
  const transit = row.arrived_on == null;
  return (
    <tr>
      <td>{row.issue_date}</td>
      <td className="pl-finance-num">{yen(row.amount_jpy)}</td>
      <td className="desc">
        <div>{row.description ?? "—"}</div>
        {row.receipts.map((r) => (
          <span key={r.id} className="pl-prepay-receipts">
            証憑: {r.description ?? `#${r.id}`}
            {r.amount != null ? `（${yen(r.amount)}）` : ""}
          </span>
        ))}
      </td>
      <td>
        <span className={`pl-badge ${badge.cls}`}>{badge.label}</span>
      </td>
      <td>
        <div className="pl-prepay-arrival">
          <input
            type="date"
            className="pl-input"
            value={row.arrived_on ?? ""}
            disabled={busy}
            onChange={(e) => onSave(row.id, e.target.value || null, note)}
            aria-label="入荷日"
          />
          {transit ? (
            <span className="pl-badge pl-badge--unconfirmed">在途</span>
          ) : (
            <button className="btn btn-sm" disabled={busy} onClick={() => onSave(row.id, null, note)}>
              未入荷に戻す
            </button>
          )}
        </div>
      </td>
      <td>
        <input
          type="text"
          className="pl-input pl-prepay-note-input"
          value={note}
          disabled={busy}
          placeholder="メモ"
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (note !== (row.note ?? "")) onSave(row.id, row.arrived_on, note);
          }}
          aria-label="メモ"
        />
      </td>
      <td>
        {row.source === "manual" && (
          <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => onDelete(row)}>
            削除
          </button>
        )}
      </td>
    </tr>
  );
}

function PrepaymentsPanel() {
  const [data, setData] = useState<PrepaymentsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncNotes, setSyncNotes] = useState<string[]>([]);
  const [syncResultText, setSyncResultText] = useState<string | null>(null);
  const [addDate, setAddDate] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [addArrived, setAddArrived] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  function load() {
    setLoadError(null);
    fetchPlPrepayments()
      .then(setData)
      .catch((e: unknown) => setLoadError(e instanceof ApiClientError ? e.message : "前払い仕入の取得に失敗しました"));
  }

  useEffect(load, []);

  async function save(id: string, arrivedOn: string | null, note: string) {
    setBusyId(id);
    setActionError(null);
    try {
      setData(await postPlPrepayment({ id, arrived_on: arrivedOn, note: note.trim() }));
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "保存に失敗しました");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(row: PrepaymentRow) {
    if (!window.confirm(`手入力の行を削除します。\n${row.issue_date} ${yen(row.amount_jpy)} ${row.description ?? ""}`)) return;
    setBusyId(row.id);
    setActionError(null);
    try {
      setData(await postPlPrepayment({ id: row.id, delete: true }));
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "削除に失敗しました");
    } finally {
      setBusyId(null);
    }
  }

  async function addRow() {
    setAddBusy(true);
    setActionError(null);
    try {
      const res = await postPlPrepayment({
        issue_date: addDate,
        amount_jpy: Math.round(Number(addAmount)),
        description: addDesc.trim() || undefined,
        arrived_on: addArrived || undefined,
      });
      setData(res);
      setAddDate("");
      setAddAmount("");
      setAddDesc("");
      setAddArrived("");
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "追加に失敗しました");
    } finally {
      setAddBusy(false);
    }
  }

  async function resync() {
    setSyncBusy(true);
    setSyncNotes([]);
    setSyncResultText(null);
    setActionError(null);
    try {
      const res = await postPlSync("freee");
      const pre = (res.result as { prepayments?: { rows: number; upserted: number; errors: string[] } } | null)?.prepayments;
      if (pre) {
        setSyncResultText(`前払い仕入 ${pre.rows}件を取得（更新 ${pre.upserted}件）`);
        setSyncNotes(pre.errors ?? []);
      }
      load();
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "freee からの再取得に失敗しました");
    } finally {
      setSyncBusy(false);
    }
  }

  const addValid = addDate !== "" && addAmount !== "" && Number.isFinite(Number(addAmount)) && Number(addAmount) > 0;

  return (
    <div className="pl-card">
      <span className="pl-card-title">前払い仕入（在途品）</span>
      <span className="pl-card-sub">
        freee の当期商品仕入のうち、輸入仕入高・仕入諸掛（関税・輸入消費税・送金手数料）・輸入運賃を台帳に載せます（いずれも倉庫の在庫報告に反映される前に支払うため、支払日ベースで計上されます）。入荷日を入れると、その日以降の月末から在途品（支払済み・未反映）から外れます。入荷日は実際の到着日ではなく、倉庫の在庫報告に反映された日を入力してください。
      </span>

      {loadError && <StateBanner kind="error" message={loadError} onRetry={load} />}
      {!data && !loadError && <StateBanner kind="loading" message="前払い仕入を取得しています" />}

      {data && (
        <>
          {data.transit_by_month.length > 0 && (
            <div className="pl-prepay-summary" aria-label="月末の在途品">
              {data.transit_by_month.map((t) => (
                <div key={t.month} className="pl-prepay-summary-item">
                  <span>{t.month} 末</span>
                  <strong>{yen(t.amount)}</strong>
                  <span style={{ color: "var(--pl-muted)" }}>{t.open_rows}件</span>
                </div>
              ))}
            </div>
          )}

          <div className="pl-csv-entry-foot">
            <button className="btn btn-sm" disabled={syncBusy} onClick={() => void resync()}>
              {syncBusy ? "取得中…（最大1分ほど）" : "freee から再取得"}
            </button>
            <span className="pl-card-sub">最終同期: {fmtTs(data.last_synced_at)}</span>
            {syncResultText && <span className="pl-csv-result pl-csv-result--ok">{syncResultText}</span>}
          </div>
          {syncNotes.length > 0 && (
            <div className="pl-csv-warnings">
              {syncNotes.map((n, i) => (
                <div key={i}>⚠ {n}</div>
              ))}
            </div>
          )}
          {actionError && <span className="pl-csv-result pl-csv-result--error">{actionError}</span>}

          {data.rows.length === 0 ? (
            <div className="pl-empty">前払い仕入がありません。「freee から再取得」または下のフォームから追加してください。</div>
          ) : (
            <div className="pl-table-wrap">
              <table className="data-table pl-import-table pl-prepay-table">
                <thead>
                  <tr>
                    <th>送金日</th>
                    <th>金額</th>
                    <th>摘要</th>
                    <th>由来</th>
                    <th>入荷日</th>
                    <th>メモ</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <PrepaymentRowView key={r.id} row={r} busy={busyId === r.id} onSave={(id, a, n) => void save(id, a, n)} onDelete={(row) => void remove(row)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <span className="pl-card-sub">手入力で追加（期首前の前払いなど、freee に出てこない送金）</span>
          <div className="pl-form-grid">
            <label className="pl-form-field">
              送金日
              <input type="date" className="pl-input" value={addDate} onChange={(e) => setAddDate(e.target.value)} />
            </label>
            <label className="pl-form-field">
              金額（円）
              <input type="number" min={1} className="pl-input" value={addAmount} onChange={(e) => setAddAmount(e.target.value)} placeholder="1800000" />
            </label>
            <label className="pl-form-field">
              入荷日（任意）
              <input type="date" className="pl-input" value={addArrived} onChange={(e) => setAddArrived(e.target.value)} />
            </label>
            <label className="pl-form-field pl-form-field--wide">
              摘要
              <input type="text" className="pl-input" value={addDesc} onChange={(e) => setAddDesc(e.target.value)} placeholder="例: 仕入先名・品目・USD額" />
            </label>
          </div>
          <div className="pl-csv-entry-foot">
            <button className="btn btn-primary btn-sm" disabled={addBusy || !addValid} onClick={() => void addRow()}>
              {addBusy ? "追加中…" : "追加"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// (b) ロジクム月額フォーム
// ---------------------------------------------------------------------------

function LogiMonthlyForm() {
  const [month, setMonth] = useState(todayMonth());
  const [shipping, setShipping] = useState("");
  const [material, setMaterial] = useState("");
  const [shipments, setShipments] = useState("");
  const [storage, setStorage] = useState("");
  const [wms, setWms] = useState("");
  const [status, setStatus] = useState<"estimate" | "actual">("estimate");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [resultText, setResultText] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const unitPrice =
    Number(shipping) > 0 && Number(shipments) > 0
      ? Math.round((Number(shipping) + Number(material)) / Number(shipments))
      : null;

  async function submit() {
    setSaving(true);
    setResultText(null);
    setErrorText(null);
    try {
      await postPlLogiMonthly({
        month,
        shipping_jpy: Math.round(Number(shipping) || 0),
        material_jpy: Math.round(Number(material) || 0),
        shipments: Math.round(Number(shipments) || 0),
        storage_jpy: storage ? Math.round(Number(storage)) : undefined,
        wms_jpy: wms ? Math.round(Number(wms)) : undefined,
        status,
        note: note || undefined,
      });
      setResultText(`${month} 保存しました${unitPrice != null ? `（1件単価 ¥${unitPrice}）` : ""}`);
    } catch (e) {
      setErrorText(e instanceof ApiClientError ? e.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pl-card">
      <span className="pl-card-title">ロジクム月額（送料・資材）</span>
      <span className="pl-card-sub">請求書PDF（p1請求書・p3資材明細）の月合計を入力。当月分は前月確定単価が見込みとして使われます。</span>
      <div className="pl-form-grid">
        <label className="pl-form-field">
          対象月
          <input type="month" className="pl-input" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <label className="pl-form-field">
          送料（税抜・円）
          <input type="number" className="pl-input" value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder="280860" />
        </label>
        <label className="pl-form-field">
          資材（税抜・円）
          <input type="number" className="pl-input" value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="71300" />
        </label>
        <label className="pl-form-field">
          梱包件数
          <input type="number" className="pl-input" value={shipments} onChange={(e) => setShipments(e.target.value)} placeholder="464" />
        </label>
        <label className="pl-form-field">
          保管料（任意・固定費）
          <input type="number" className="pl-input" value={storage} onChange={(e) => setStorage(e.target.value)} />
        </label>
        <label className="pl-form-field">
          WMS（任意・固定費）
          <input type="number" className="pl-input" value={wms} onChange={(e) => setWms(e.target.value)} />
        </label>
        <label className="pl-form-field">
          状態
          <select className="pl-input" value={status} onChange={(e) => setStatus(e.target.value as "estimate" | "actual")}>
            <option value="estimate">見込み（前月単価流用）</option>
            <option value="actual">確定（請求書）</option>
          </select>
        </label>
        <label className="pl-form-field pl-form-field--wide">
          メモ
          <input type="text" className="pl-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="例: 着払い運賃は別枠のため含めない" />
        </label>
      </div>
      {unitPrice != null && <div className="pl-csv-preview">1件単価（送料+資材）÷件数 ＝ ¥{unitPrice}</div>}
      <div className="pl-csv-entry-foot">
        <button className="btn btn-primary btn-sm" disabled={saving || !month || !shipments} onClick={() => void submit()}>
          {saving ? "保存中…" : "保存"}
        </button>
        {resultText && <span className="pl-csv-result pl-csv-result--ok">{resultText}</span>}
        {errorText && <span className="pl-csv-result pl-csv-result--error">{errorText}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// (c) 料率・単価設定
// ---------------------------------------------------------------------------

type SettingsLoadable = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; settings: PlSetting[] };

function SettingsPanel() {
  const [state, setState] = useState<SettingsLoadable>({ status: "loading" });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [confirmedDrafts, setConfirmedDrafts] = useState<Record<string, boolean>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    fetchPlSettings()
      .then((res) => setState({ status: "ok", settings: res.settings }))
      .catch((e: unknown) => setState({ status: "error", message: e instanceof ApiClientError ? e.message : "料率設定の取得に失敗しました" }));
  }, []);

  async function save(setting: PlSetting) {
    const value = drafts[setting.key] ?? setting.value;
    // review §4-4: 保存が常に confirmed=true にならないよう、チェックボックスの値（未操作なら既存値）を送る。
    const confirmed = confirmedDrafts[setting.key] ?? setting.confirmed;
    setSavingKey(setting.key);
    try {
      await postPlSetting(setting.key, value, confirmed);
      // 実 worker の POST は { ok: true } のみ返す（settings を含まない）ため、保存後は GET し直して表示を更新する
      const fresh = await fetchPlSettings();
      setState({ status: "ok", settings: fresh.settings });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[setting.key];
        return next;
      });
      setConfirmedDrafts((prev) => {
        const next = { ...prev };
        delete next[setting.key];
        return next;
      });
    } catch {
      // 失敗時はドラフトを残す（再試行可能にする）
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="pl-card">
      <span className="pl-card-title">料率・単価設定</span>
      <span className="pl-card-sub">見込み計算に使う料率・単価（pl_settings）。値は1か所で管理。確定チェックが外れている項目は「未確認」バッジ付きでダッシュボードに表示されます。</span>
      {state.status === "loading" && <div className="pl-empty">読み込み中…</div>}
      {state.status === "error" && <div className="pl-empty">{state.message}</div>}
      {state.status === "ok" && (
        <div className="pl-table-wrap">
          <table className="data-table pl-settings-table">
            <thead>
              <tr>
                <th>設定</th>
                <th>値</th>
                <th>確定</th>
                <th>更新日時</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {state.settings.map((s) => (
                <tr key={s.key}>
                  <td>
                    <div>{s.label}</div>
                    <div className="pl-settings-key">{s.key}</div>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="pl-input pl-settings-input"
                      value={drafts[s.key] ?? s.value}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [s.key]: e.target.value }))}
                    />
                  </td>
                  <td>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={confirmedDrafts[s.key] ?? s.confirmed}
                        onChange={(e) => setConfirmedDrafts((prev) => ({ ...prev, [s.key]: e.target.checked }))}
                      />
                      {(confirmedDrafts[s.key] ?? s.confirmed) ? (
                        <span className="pl-badge">確定</span>
                      ) : (
                        <span className="pl-badge pl-badge--unconfirmed">未確認</span>
                      )}
                    </label>
                  </td>
                  <td>{s.updated_at && !Number.isNaN(new Date(s.updated_at).getTime()) ? new Date(s.updated_at).toLocaleString("ja-JP") : "—"}</td>
                  <td>
                    <button className="btn btn-sm" disabled={savingKey === s.key} onClick={() => void save(s)}>
                      {savingKey === s.key ? "保存中…" : "保存"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// (d) 取込状況表（既存プレースホルダーを維持）
// ---------------------------------------------------------------------------

function ImportStatusTable({ data }: { data: PlImportsResponse }) {
  return (
    <div className="pl-card">
      <span className="pl-card-title">取込状況</span>
      {data.rows.length === 0 ? (
        <div className="pl-empty">取込データがありません。</div>
      ) : (
        <div className="pl-table-wrap">
          <table className="data-table pl-import-table">
            <thead>
              <tr>
                <th>種別</th>
                <th>月</th>
                <th>件数</th>
                <th>状態</th>
                <th>最終取込</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.kind}-${r.month ?? "all"}-${i}`}>
                  <td>{KIND_LABEL[r.kind] ?? r.kind}</td>
                  <td>{r.month ?? "—"}</td>
                  <td>{r.rows.toLocaleString("ja-JP")}</td>
                  <td>{STATUS_LABEL[r.status] ?? r.status}</td>
                  <td>{r.last_imported_at ? new Date(r.last_imported_at).toLocaleString("ja-JP") : "未取込"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pl-aux-cards">
        <div className="pl-aux-card">
          <span className="pl-aux-card-title">原価スナップショット</span>
          <span>{data.freshness.cost_snapshot ? new Date(data.freshness.cost_snapshot).toLocaleString("ja-JP") : "未取得"}</span>
        </div>
        <div className="pl-aux-card">
          <span className="pl-aux-card-title">Meta広告費</span>
          <span>{data.freshness.meta_spend ? new Date(data.freshness.meta_spend).toLocaleString("ja-JP") : "未取得"}</span>
        </div>
        <div className="pl-aux-card">
          <span className="pl-aux-card-title">Google広告費</span>
          <span>{data.freshness.google_spend ? new Date(data.freshness.google_spend).toLocaleString("ja-JP") : "未取得"}</span>
        </div>
        <div className="pl-aux-card">
          <span className="pl-aux-card-title">注文単位の経済性</span>
          <span>{data.freshness.order_finance ? new Date(data.freshness.order_finance).toLocaleString("ja-JP") : "未取得"}</span>
        </div>
        <div className="pl-aux-card">
          <span className="pl-aux-card-title">freee 試算表</span>
          <span>{data.freshness.freee ? new Date(data.freshness.freee).toLocaleString("ja-JP") : "未取得"}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// エントリポイント
// ---------------------------------------------------------------------------

export function ImportTab({ data }: { data: PlImportsResponse }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SyncNowPanel />
      <FreeeConnectPanel />
      <InventoryMonthEndForm />
      <PrepaymentsPanel />
      <CsvImportPanel />
      <LogiMonthlyForm />
      <SettingsPanel />
      <ImportStatusTable data={data} />
    </div>
  );
}
